import { Injectable, SecurityContext, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Campaign } from '@longnecktech/splash-commons-fe';
import { CampaignSummary } from '@shared/types/campaign-summary';
import { Observable, BehaviorSubject, tap, catchError, throwError } from 'rxjs';
import { environment } from '@environment';
import { map } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import {
  addNewItemToState,
  updateItemInState,
  deleteItemFromState,
} from '@shared/utils/state.util';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root',
})
export class CampaignsService {
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private notificationService = inject(NotificationService);

  private _campaigns = new BehaviorSubject<Campaign[]>([]);
  private _campaignsSummary = new BehaviorSubject<CampaignSummary[]>([]);
  private _isLoading = new BehaviorSubject<boolean>(false);

  readonly campaigns$ = this._campaigns.asObservable();
  readonly campaignsSummary$ = this._campaignsSummary.asObservable();
  readonly isLoading$ = this._isLoading.asObservable();

  fetchCampaignsSummary(): Observable<CampaignSummary[]> {
    if (this._campaignsSummary.value.length > 0) {
      return this.campaignsSummary$;
    }
    this._isLoading.next(true);
    return this.http
      .get<
        CampaignSummary[]
      >(environment.backendUrl + `/api/admin/campaign/all/summary`)
      .pipe(
        tap((campaigns) => {
          this._isLoading.next(false);
          this._campaignsSummary.next(campaigns);
        }),
        catchError((err: Error) => {
          this._isLoading.next(false);
          return throwError(() => new Error(err.message));
        }),
      );
  }

  fetchAllCampaigns(): Observable<Campaign[]> {
    if (this._campaigns.value.length > 0) {
      return this.campaigns$;
    }
    this._isLoading.next(true);
    return this.http
      .get<Campaign[]>(environment.backendUrl + '/api/admin/campaign/all')
      .pipe(
        map((campaigns) =>
          campaigns.map((campaign) => ({
            ...campaign,
            slides: campaign.slides.map((s) => ({
              ...s,
              html: this.sanitizer.sanitize(
                SecurityContext.HTML,
                s.html || null,
              ) as string,
            })),
          })),
        ),
        tap((campaigns) => {
          this._isLoading.next(false);
          this._campaigns.next(campaigns);
        }),
        catchError((err: Error) => {
          this._isLoading.next(false);
          return throwError(() => new Error(err.message));
        }),
      );
  }

  createCampaign(campaign: Partial<Campaign>): Observable<Campaign> {
    this._isLoading.next(true);
    return this.http
      .post<Campaign>(environment.backendUrl + '/api/admin/campaign', {
        ...campaign,
        slides: campaign.slides?.map((s) => ({
          ...s,
          html: this.sanitizer.sanitize(
            SecurityContext.HTML,
            s.html || null,
          ) as string,
        })),
      })
      .pipe(
        tap((response) => addNewItemToState(this._campaigns, response)),
        tap(() => this._isLoading.next(false)),
        tap(() =>
          this.notificationService.showNotification(
            'campaignDialog.campaignCreateSuccess',
          ),
        ),
        catchError((err: Error) => {
          this._isLoading.next(false);
          this.notificationService.showNotification(
            'campaignDialog.campaignCreateError',
          );
          return throwError(
            () => new Error(err?.message || 'Error creating the campaign'),
          );
        }),
      );
  }

  updateCampaign(campaign: Campaign): Observable<Campaign> {
    this._isLoading.next(true);
    return this.http
      .put<Campaign>(environment.backendUrl + '/api/admin/campaign', {
        ...campaign,
        slides: campaign.slides.map((s) => ({
          ...s,
          html: this.sanitizer.sanitize(
            SecurityContext.HTML,
            s.html || null,
          ) as string,
        })),
      })
      .pipe(
        tap((updated) => updateItemInState(this._campaigns, updated)),
        tap(() => this._isLoading.next(false)),
        tap(() =>
          this.notificationService.showNotification(
            'campaignDialog.campaignUpdateSuccess',
          ),
        ),
        catchError((err: Error) => {
          this._isLoading.next(false);
          this.notificationService.showNotification(
            'campaignDialog.campaignUpdateError',
          );
          return throwError(
            () => new Error(err?.message || 'Error updating the campaign'),
          );
        }),
      );
  }

  deleteCampaign(uuid: string): Observable<void> {
    return this.http
      .delete<void>(environment.backendUrl + '/api/admin/campaign/' + uuid)
      .pipe(tap(() => deleteItemFromState(this._campaigns, uuid)));
  }

  resetData(): void {
    this._campaigns.next([]);
    this._campaignsSummary.next([]);
  }
}
